import { Form, Input, InputNumber, Select, Switch } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { SettingMeta } from "../config/settingsMeta";
import type { SettingValue } from "../types";

interface SettingFieldRendererProps {
	settingKey: string;
	meta: SettingMeta;
	/** Qoralama qiymati bo'lsa o'sha, aks holda serverdagi qiymat. */
	value: SettingValue;
	onChange: (value: SettingValue) => void;
}

/**
 * `Intl.supportedValuesOf("timeZone")` — RUNTIME API, build vaqtida emas,
 * komponent mount'ida hisoblanadi (spec §4). Modul darajasida bir marta
 * keshlanadi: ro'yxat sahifa umrida o'zgarmaydi, har render/mount uni qayta
 * hisoblash keraksiz.
 *
 * ⚠️ Backend `isKnownTimeZone` (`settings.schemas.ts`) bu ro'yxatga
 * TAYANMAYDI — u eskirgan aliaslarni (`Zulu`, `W-SU`, `Asia/Calcutta`) ham
 * qabul qiladi, `Intl.supportedValuesOf` esa ularni tashlab yuboradi.
 * Shu sababli tanlov ro'yxati serverdagi JORIY qiymatni albatta o'z ichiga
 * olishi kerak — `timezoneOptions()` buni pastda qo'shadi, aks holda eski
 * aliasda saqlangan zona Select'da BO'SH ko'rinardi va admin uni "hech
 * narsa tanlanmagan" deb o'ylab, aslida ishlayotgan sozlamani xatoga
 * chiqarardi.
 */
let cachedTimeZones: string[] | null = null;

function allTimeZones(): string[] {
	if (cachedTimeZones) {
		return cachedTimeZones;
	}

	try {
		cachedTimeZones = Intl.supportedValuesOf("timeZone");
	} catch {
		cachedTimeZones = [];
	}

	return cachedTimeZones;
}

function timezoneOptions(current: string): { value: string; label: string }[] {
	const base = allTimeZones();
	const withCurrent = current && !base.includes(current) ? [current, ...base] : base;

	return withCurrent.map((zone) => ({ value: zone, label: zone }));
}

/** `report_summary_cron` uchun frontend oldindan-tutish — yakuniy tekshiruv baribir backendda (`isValidCron`). */
function cronShapeOk(value: string): boolean {
	return value.trim().split(/\s+/).length === 5;
}

function toNumber(value: SettingValue): number {
	return typeof value === "number" ? value : Number(value) || 0;
}

function toBoolean(value: SettingValue): boolean {
	return typeof value === "boolean" ? value : value === "true";
}

function toText(value: SettingValue): string {
	return typeof value === "string" ? value : String(value ?? "");
}

/**
 * `meta.type` bo'yicha tur-ga mos editor (spec §4). Har turdagi qiymatni
 * `SettingValue` ga MOSLAB qaytaradi — `onChange` chaqiruvchisi (sahifa)
 * qoralamaga xom holda yozadi, ya'ni bu yerdagi coercion yagona joy.
 */
export function SettingFieldRenderer({
	settingKey,
	meta,
	value,
	onChange,
}: SettingFieldRendererProps) {
	const { t } = useTranslation("settings");
	const timezoneOpts = useMemo(() => timezoneOptions(toText(value)), [value]);

	if (meta.type === "number") {
		return (
			<InputNumber
				className="w-full"
				value={toNumber(value)}
				min={meta.min}
				max={meta.max}
				step={meta.step}
				onChange={(next) => onChange(next ?? meta.min)}
				aria-label={settingKey}
			/>
		);
	}

	if (meta.type === "boolean") {
		return <Switch checked={toBoolean(value)} onChange={onChange} aria-label={settingKey} />;
	}

	if (meta.type === "enum") {
		const options = meta.options.map((option) => ({
			value: option.value,
			label: t(option.labelKey),
		}));

		return (
			<Select
				className="w-full"
				value={toText(value)}
				options={options}
				onChange={onChange}
				aria-label={settingKey}
			/>
		);
	}

	if (meta.type === "timezone") {
		return (
			<Select
				className="w-full"
				showSearch
				value={toText(value)}
				options={timezoneOpts}
				onChange={onChange}
				aria-label={settingKey}
			/>
		);
	}

	// meta.type === "cron"
	const cronValue = toText(value);
	const shapeOk = cronValue.trim().length === 0 || cronShapeOk(cronValue);

	return (
		<Form.Item
			className="mb-0"
			validateStatus={shapeOk ? "" : "error"}
			help={shapeOk ? undefined : t("cron.invalidHint")}
		>
			<Input
				value={cronValue}
				onChange={(event) => onChange(event.target.value)}
				placeholder="0 19 * * *"
				aria-label={settingKey}
			/>
		</Form.Item>
	);
}

export { cronShapeOk };
